import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Typography, Button, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

const SignaturePad = ({ isReadOnly, onSignatureChange }) => {
  const signatureRef = useRef(null);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');

  const handleEnd = () => {
    if (signatureRef.current) {
      onSignatureChange(signatureRef.current.toDataURL());
    }
  };

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      onSignatureChange('');
    }
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSignatureMode(newMode);
      // Clear previous signature when switching modes
      onSignatureChange('');
      setTypedName('');
      if (signatureRef.current) {
        signatureRef.current.clear();
      }
    }
  };

  const handleTypedNameChange = (event) => {
    const name = event.target.value;
    setTypedName(name);
    if (name.trim()) {
      // Create a simple text signature by converting to data URL
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 300;
      canvas.height = 100;
      
      // Set background
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Set text style
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      // Draw the name
      ctx.fillText(name, 20, 50);
      
      // Convert to data URL
      const dataURL = canvas.toDataURL('image/png');
      onSignatureChange(dataURL);
    } else {
      onSignatureChange('');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-inner">
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Please sign below to confirm your agreement
      </Typography>
      
      {/* Signature Mode Toggle */}
      {!isReadOnly && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Choose signature method:
          </Typography>
          <ToggleButtonGroup
            value={signatureMode}
            exclusive
            onChange={handleModeChange}
            aria-label="signature mode"
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: '#e5e7eb',
                color: '#6b7280',
                '&.Mui-selected': {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderColor: '#3b82f6',
                  '&:hover': {
                    backgroundColor: '#2563eb',
                  },
                },
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
              },
            }}
          >
            <ToggleButton value="draw" aria-label="draw signature">
              ✏️ Draw Signature
            </ToggleButton>
            <ToggleButton value="type" aria-label="type name">
              ⌨️ Type Name
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Drawing Mode */}
      {signatureMode === 'draw' && (
        <>
          <Box
            sx={{
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              '&:hover': {
                borderColor: '#3b82f6',
              },
            }}
          >
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 500,
                height: 200,
                className: 'signature-canvas',
              }}
              disabled={isReadOnly}
              onEnd={handleEnd}
            />
          </Box>
          {!isReadOnly && (
            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{
                mt: 2,
                color: '#ef4444',
                borderColor: '#ef4444',
                '&:hover': {
                  borderColor: '#dc2626',
                  backgroundColor: '#fee2e2',
                },
              }}
            >
              Clear Signature
            </Button>
          )}
        </>
      )}

      {/* Typing Mode */}
      {signatureMode === 'type' && (
        <Box sx={{ spaceY: 2 }}>
          <TextField
            fullWidth
            label="Enter your full name"
            value={typedName}
            onChange={handleTypedNameChange}
            disabled={isReadOnly}
            placeholder="e.g., John Smith"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                '&:hover': {
                  backgroundColor: 'white',
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused': {
                  backgroundColor: 'white',
                  borderColor: '#3b82f6',
                },
              },
            }}
          />
          {typedName && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                minHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 'bold',
                  color: '#1f2937',
                }}
              >
                {typedName}
              </Typography>
            </Box>
          )}
          {!isReadOnly && typedName && (
            <Button
              variant="outlined"
              onClick={() => {
                setTypedName('');
                onSignatureChange('');
              }}
              sx={{
                mt: 2,
                color: '#ef4444',
                borderColor: '#ef4444',
                '&:hover': {
                  borderColor: '#dc2626',
                  backgroundColor: '#fee2e2',
                },
              }}
            >
              Clear Name
            </Button>
          )}
        </Box>
      )}
    </div>
  );
};

export default SignaturePad; 